/**
 * Content zoom — the browser gesture (Ctrl+wheel, Ctrl +/-, Ctrl+0), scoped to
 * what the user is reading rather than to the whole window.
 *
 * **This does NOT zoom the app.** The owner's requirement, in their words: the
 * terminal, the board that holds the cards, a document — those scale; the rail
 * icons, the titlebar, the footer and every tab strip (Collections' request
 * tabs, the Boards tabs) stay exactly the size they are. Chrome that resizes
 * with the content is how a window stops being navigable at 200%: the rail
 * becomes a wall of huge icons and the tab strip eats the panel it labels.
 *
 * So this module sets ONE custom property, `--app-content-zoom`, and
 * `appZoom.css` names the containers that consume it. Adding a panel is a line
 * of CSS there, never a change here — and anything not named is chrome by
 * default, which is the safe direction to be wrong in.
 *
 * **CSS `zoom`, not the webview's native zoom.** Tauri's `webview.setZoom`
 * scales the whole window by definition, which is the thing being avoided, and
 * would also need a `core:webview:*` grant in `capabilities/default.json`,
 * whose stated doctrine is that a capability is listed only when the JS needs
 * it by name. `zoom` is supported by both webviews this app ships on
 * (WebView2/Chromium and WKWebView), reflows rather than scaling a bitmap, and
 * — unlike `transform: scale()` — leaves hit-testing and scrollbars correct.
 *
 * The steps are the ones browsers use. Free-form factors are deliberately not
 * offered: every step here is a value the layout has a fighting chance of
 * looking deliberate at.
 */

export const ZOOM_STEPS = [0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5];

export const DEFAULT_ZOOM = 1;

const STORAGE_KEY = 'apiboo-app-zoom';
// Per rail page (owner, 13.10.2026: "zoom per rail icon"): { [pageId]: factor }.
// A page never zoomed falls back to STORAGE_KEY — the one zoom every page
// shared before — so nobody's existing zoom jumps back to 100% on update.
const PAGES_KEY = 'apiboo-app-zoom-pages';

function readPages() {
  try {
    const parsed = JSON.parse(localStorage.getItem(PAGES_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/** The nearest legal step to `value`, so a stored or stale factor can never
 *  leave the UI at a size no keystroke can get it out of. */
export function nearestStep(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_ZOOM;
  let best = ZOOM_STEPS[0];
  for (const step of ZOOM_STEPS) {
    if (Math.abs(step - n) < Math.abs(best - n)) best = step;
  }
  return best;
}

/** One step in `direction` (+1 in, -1 out); saturates at the ends. */
export function stepFrom(current, direction) {
  const here = ZOOM_STEPS.indexOf(nearestStep(current));
  const next = here + (direction > 0 ? 1 : -1);
  if (next < 0 || next >= ZOOM_STEPS.length) return ZOOM_STEPS[here];
  return ZOOM_STEPS[next];
}

/** The zoom of rail page `page` (the active panel id), or the shared one. */
export function loadZoom(page) {
  if (page) {
    const pages = readPages();
    if (pages[page] != null) return nearestStep(pages[page]);
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null) return DEFAULT_ZOOM;
    return nearestStep(raw);
  } catch {
    // A blocked or full localStorage must never leave the app unrendered.
    return DEFAULT_ZOOM;
  }
}

export function saveZoom(value, page) {
  try {
    if (page) {
      localStorage.setItem(PAGES_KEY, JSON.stringify({ ...readPages(), [page]: nearestStep(value) }));
      return;
    }
    localStorage.setItem(STORAGE_KEY, String(nearestStep(value)));
  } catch {
    /* preference only — losing it costs a keystroke, not data */
  }
}

/**
 * Paint it — by publishing the factor, not by applying it.
 *
 * `--app-content-zoom` is read by the selectors in `appZoom.css`; nothing is
 * zoomed here. 100% removes both marks rather than writing `1`, so the default
 * state leaves nothing behind to puzzle over in devtools.
 *
 * `data-app-zoom` mirrors the factor because the custom property alone is not
 * observable everywhere: **jsdom drops unknown declarations silently**, so a
 * test asserting the style would pass against an `applyZoom` that did nothing.
 * The attribute is the honest record, and doubles as a styling hook for
 * anything that needs to know the window is zoomed at all.
 */
export function applyZoom(value) {
  const zoom = nearestStep(value);
  const root = typeof document !== 'undefined' ? document.documentElement : null;
  if (!root) return zoom;
  if (zoom === DEFAULT_ZOOM) {
    root.style.removeProperty('--app-content-zoom');
    root.removeAttribute('data-app-zoom');
  } else {
    root.style.setProperty('--app-content-zoom', String(zoom));
    root.setAttribute('data-app-zoom', String(zoom));
  }
  return zoom;
}
